import XMLView from "sap/ui/core/mvc/XMLView";
import BaseController from "./BaseController";
import { Router$RouteMatchedEvent } from "sap/ui/core/routing/Router";
import { AuthService } from "../service/Auth.service";
import Log from "sap/base/Log";
import { Avatar$PressEvent } from "sap/m/Avatar";
import Popover from "sap/m/Popover";
import { ButtonType, PlacementType } from "sap/m/library";
import Button, { Button$PressEvent } from "sap/m/Button";
import JSONModel from "sap/ui/model/json/JSONModel";
import Dialog from "sap/m/Dialog";
import List from "sap/m/List";
import StandardListItem from "sap/m/StandardListItem";
import { EnvironmentService } from "../service/Environment.service";
import History from "sap/ui/core/routing/History";
import { ErrorHandler } from "../service/ErrorHandler.service";
/**
 * @namespace com.unitrade.crm.controller
 */
export default class App extends BaseController {
  private _authService = new AuthService();
  private _model = new JSONModel();
  private _versionInfo = new JSONModel();
  private _oVersionDialog: Dialog;
  private avatarPopover: Popover = null;
  private _navBackFunc: () => void;

  protected oLogger = Log.getLogger("com.unitrade.crm.controller.App");

  public async onInit(): Promise<void> {
    // apply content density mode to root view
    this.getView().addStyleClass(this.getOwnerComponent().getContentDensityClass());
    this.getOwnerComponent().getRouter().attachRouteMatched(this._onRouteMatched);

    this._model = new JSONModel({
      isAvatarVisible: false,
      avatarInitials: "",
      versionLoading: false,
    });

    this.setModel(this._versionInfo, "version");
    this.setModel(this._model);
  }

  onAfterRendering(): void {
    const backbutton = this.byId("backbutton") as Button;
    backbutton.attachBrowserEvent("taphold", () => {
      this.getRouter().navTo("home", {}, undefined, true);
    });
  }

  public handleAvatarPress(event: Avatar$PressEvent) {
    if (this.avatarPopover === null) {
      this.avatarPopover = new Popover({
        showHeader: false,
        placement: PlacementType.Bottom,
        content: [
          new Button({
            text: "Logout",
            type: ButtonType.Transparent,
            press: this.handleLogoutButtonPress.bind(this),
          }),
          new Button({
            text: "Versionsinformationen",
            type: ButtonType.Transparent,
            press: this.handleVersionButtonPress.bind(this),
          }),
          new Button({
            text: "Einstellungen",
            type: ButtonType.Transparent,
            press: this.handleSettingsButtonBress.bind(this),
          }),
        ],
      }).addStyleClass("sapMOTAPopover sapTntToolHeaderPopover");
    }

    if (this.avatarPopover.isOpen()) {
      this.avatarPopover.close();
    } else {
      this.avatarPopover.openBy(event.getSource());
    }
  }

  public async handleSettingsButtonBress(event: Button$PressEvent) {
    this.getRouter().navTo("settings");
  }

  public async handleVersionButtonPress(event: Button$PressEvent) {
    this._model.setProperty("/versionLoading", true);

    if (!this._oVersionDialog) {
      this._oVersionDialog = new Dialog({
        busy: "{/versionLoading}",
        title: "Versions-Information",
        content: new List({
          items: {
            path: "version>/collection",
            template: new StandardListItem({
              title: "{version>key}",
              description: "{version>value}",
            }),
          },
        }),
        endButton: new Button({
          text: "Schließen",
          press: () => {
            this._oVersionDialog.close();
          },
        }),
      });

      this.getView().addDependent(this._oVersionDialog);
    }

    this._versionInfo.setData({});
    try {
      const versionInfo = await $.get(`${EnvironmentService.getBaseUrl()}/status`);
      const userConfig = this._authService.getUserConfiguration();
      const aVersionInfos = [];
      aVersionInfos.push({ key: "Datenbank-Version", value: versionInfo.dbVersion });
      aVersionInfos.push({ key: "Ungültige Datenbank Objekte", value: versionInfo.invalidDbObjectsCount });
      aVersionInfos.push({ key: "MSG 100", value: versionInfo.msg100 });

      aVersionInfos.push({ key: "Server-Version", value: versionInfo.apiVersion });
      aVersionInfos.push({ key: "Server-Build-Datum", value: versionInfo.buildDate });

      aVersionInfos.push({ key: "Client-Version", value: this.getVersion().getProperty("/buildVersion") });
      aVersionInfos.push({ key: "Client-Build-Date", value: this.getVersion().getProperty("/buildDate") });
      aVersionInfos.push({ key: "Experimentelle Features aktiviert", value: userConfig.experimentalFeaturesEnabled ? "Ja" : "Nein" });
      this._versionInfo.setData({ collection: aVersionInfos });
      this._oVersionDialog.open();
    } catch (ex: any) {
      ErrorHandler.handleError(ex, { message: "Fehler beim Laden der Versionsdaten" });
    } finally {
      this._model.setProperty("/versionLoading", false);
    }
  }

  public handleLogoutButtonPress(event: Button$PressEvent) {
    this._authService.logoff();
    (<JSONModel>this.getModel()).setProperty("/isAvatarVisible", false);
    AuthService.redirectToLogin(this.getRouter(), "", {});
  }

  private _onRouteMatched = async (oEvent: Router$RouteMatchedEvent) => {
    this.oLogger.info("Route matched: " + oEvent.getParameter("name"));
    if (!this._authService.isAuthenticated()) {
      const sRouteName = oEvent.getParameter("name");
      if (sRouteName !== "login" && sRouteName !== "loginWithRedirect") {
        Log.warning(`Unauthorized user trying to access ${sRouteName}. Redirecting to login.`, null, this.getView().getControllerName());
        (<JSONModel>this.getModel()).setProperty("/isAvatarVisible", false);
        (<JSONModel>this.getModel()).setProperty("/isChatButtonVisible", false);
        (<JSONModel>this.getModel()).setProperty("/avatarInitials", "");
        const params = oEvent.getParameters();
        AuthService.redirectToLogin(this.getRouter(), sRouteName, params.arguments);
      }
    } else {
      const user = this._authService.getLoggedInUser();
      const config = this._authService.getUserConfiguration();
      (<JSONModel>this.getModel()).setProperty("/isAvatarVisible", true);
      (<JSONModel>this.getModel()).setProperty("/isChatButtonVisible", config.experimentalFeaturesEnabled);
      (<JSONModel>this.getModel()).setProperty("/avatarInitials", user.Username.substring(0, 3).toUpperCase());
    }

    const view = oEvent.getParameter("view") as XMLView;
    const controller = view.getController() as BaseController;
    if (controller) {
      await controller.setTitle();
      await controller.setNavButtonEnabled();
      this._navBackFunc = controller.navBackFunc;
    }
  };

  /**
   * Convenience event handler for navigating back.
   * It there is a history entry we go one step back in the browser history
   * If not, it will replace the current entry of the browser history with the main route.
   */
  public onNavBack(): void {
    if (this._navBackFunc) {
      this._navBackFunc();
      return;
    }

    const sPreviousHash = History.getInstance().getPreviousHash();
    const router = this.getOwnerComponent().getRouter();
    const routeInfo = router.getRouteInfoByHash(sPreviousHash);
    if (sPreviousHash !== undefined && routeInfo.name !== "login" && routeInfo.name !== "loginWithRedirect") {
      window.history.go(-1);
    } else {
      router.navTo("home", {}, undefined, true);
    }
  }

  public handleChatButtonPress = (oEvent: Button$PressEvent) => {
    this.getRouter().navTo("chat");
  };
}
